import React, { useState, useEffect, useRef } from 'react';
import ProductCatalogMobile from './ProductCatalogMobile';

const Mobilesecurity = () => {
  const [isVisible, setIsVisible] = useState({});

  const sectionRef= useRef(null);

  const scrollToRef=()=>{
    sectionRef.current?.scrollIntoView({behavior:'smooth'})
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    // Observe elements with IDs
    const elements = document.querySelectorAll('[id^="animate-"]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: "fas fa-mobile-alt",
      title: "Mobile & Tablet Security",
      description: "Advanced security stands with recoiler systems, sensor cables, and adjustable brackets for smartphones and tablets."
    },
    {
      icon: "fas fa-camera",
      title: "Camera Protection", 
      description: "Specialized holder stands and lens protection systems designed for camera and photography equipment security."
    },
    {
      icon: "fas fa-headphones",
      title: "Audio Accessories",
      description: "Secure display solutions for headphones, earphones, and audio equipment with charging capabilities."
    }
  ];

  const features = [
    {
      icon: "fas fa-lock",
      title: "High Security",
      description: "Advanced locking mechanisms and steel wire systems ensure maximum protection"
    },
    {
      icon: "fas fa-cog", 
      title: "Adjustable Design",
      description: "Flexible brackets and clamps accommodate various device sizes and types"
    },
    {
      icon: "fas fa-bolt",
      title: "Charging Integration", 
      description: "Built-in charging capabilities with multiple connector types (Micro, USB-C, Lightning)"
    },
    {
      icon: "fas fa-volume-up",
      title: "Alarm System",
      description: "100dB alarm volume with remote control functionality for enhanced security"
    }
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="mobile-security-landing">
      <style jsx>{`
        :root {
          --primary-color: #0f2027;
          --secondary-color: #203a43;
          --accent-color: #3b82f6;
          --text-dark: #1f2937;
          --text-light: #6b7280;
          --bg-light: #f8fafc;
        }

        .hero-section {
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
          min-height: 75vh;
          position: relative;
          overflow: hidden;
        }

        .hero-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
          opacity: 0.3;
        }

        .hero-content {
          position: relative;
          z-index: 2;
        }

        .floating-elements {
          position: absolute;
          width: 100%;
          height: 100%;
          overflow: hidden;
          z-index: 1;
        }

        .floating-icon {
          position: absolute;
          color: rgba(255,255,255,0.1);
          animation: float 6s ease-in-out infinite;
        }

        .floating-icon:nth-child(1) { top: 20%; left: 10%; animation-delay: 0s; }
        .floating-icon:nth-child(2) { top: 60%; right: 15%; animation-delay: 2s; }
        .floating-icon:nth-child(3) { bottom: 30%; left: 20%; animation-delay: 4s; }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        .btn-primary-custom {
          background: linear-gradient(45deg, #ff6b6b, #ee5a24);
          border: none;
          padding: 15px 40px;
          font-size: 1.1rem;
          font-weight: 600;
          border-radius: 50px;
          transition: all 0.3s ease;
          box-shadow: 0 8px 25px rgba(238, 90, 36, 0.4);
          color: white;
          text-decoration: none;
        }

        .btn-primary-custom:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(238, 90, 36, 0.6);
          color: white;
        }

        .service-card {
          background: white;
          border-radius: 20px;
          padding: 2.5rem;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          border: none;
          height: 100%;
          opacity: 0;
          transform: translateY(30px);
        }

        .service-card.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .service-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.15);
        }

        .service-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(45deg, var(--primary-color), var(--accent-color));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          font-size: 2rem;
          color: white;
        }

        .feature-item {
          display: flex;
          align-items: center;
          margin-bottom: 2rem;
          opacity: 0;
          transform: translateX(-30px);
          transition: all 0.6s ease;
        }

        .feature-item.visible {
          opacity: 1;
          transform: translateX(0);
        }

        .feature-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(45deg, #10b981, #059669);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.5rem;
          font-size: 1.5rem;
          color: white;
          flex-shrink: 0;
        }

        .contact-info {
          background: rgba(255,255,255,0.1);
          border-radius: 15px;
          padding: 2rem;
          margin-top: 3rem;
        }

        .pulse {
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        @media (max-width: 768px) {
          .service-card { margin-bottom: 2rem; }
          .feature-item { flex-direction: column; text-align: center; }
          .feature-icon { margin-right: 0; margin-bottom: 1rem; }
        }
      `}</style>

      {/* Hero Section */}
      <section className="hero-section d-flex align-items-center">
        <div className="floating-elements">
          <i className="fas fa-mobile-alt floating-icon fa-3x"></i>
          <i className="fas fa-tablet-alt floating-icon fa-4x"></i>
          <i className="fas fa-headphones floating-icon fa-3x"></i>
        </div>
        
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="hero-content">
                <h1 className="display-3 fw-bold text-white mb-4">
                  Secure Your Electronics
                </h1>
                <p className="lead text-white-50 mb-4">
                  Professional security solutions for mobile devices, tablets, cameras, and electronic accessories. 
                  Protect your valuable inventory with our innovative stands and sensor systems.
                </p>
                <button 
                  className="btn btn-primary-custom"
                  onClick={() => scrollToSection('categoriesmobile')}
                >
                  Explore Solutions <i className="fas fa-arrow-right ms-2"></i>
                </button>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="text-center">
                <i className="fas fa-shield-alt pulse" style={{fontSize: '10rem', color: 'rgba(255,255,255,0.3)'}}></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-5" style={{backgroundColor: 'var(--bg-light)'}}>
        <div className="container">
          <h2 className="display-6 fw-bold text-center mb-5">Our Security Solutions</h2>
          <div className="row">
            {services.map((service, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <div 
                  id={`animate-service-${index}`}
                  className={`service-card ${isVisible[`animate-service-${index}`] ? 'visible' : ''}`}
                >
                  <div className="service-icon">
                    <i className={service.icon}></i>
                  </div>
                  <h3 className="h4 fw-semibold mb-3">{service.title}</h3>
                  <p className="text-muted">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <h2 className="display-6 fw-bold mb-4">Why Choose Our Solutions?</h2>
              {features.map((feature, index) => (
                <div 
                  key={index}
                  id={`animate-feature-${index}`}
                  className={`feature-item ${isVisible[`animate-feature-${index}`] ? 'visible' : ''}`}
                >
                  <div className="feature-icon">
                    <i className={feature.icon}></i>
                  </div>
                  <div className="feature-content">
                    <h4 className="h5 fw-semibold mb-2">{feature.title}</h4>
                    <p className="text-muted mb-0">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="col-lg-6">
              <div className="text-center">
                <i className="fas fa-shopping-cart" style={{fontSize: '15rem', color: 'var(--bg-light)', marginTop: '50px'}}></i>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id='categoriesmobile'>
        <ProductCatalogMobile/>
      </section>

      {/* CTA Section */}
      <section className="py-5 text-center text-white" style={{background: 'linear-gradient(135deg, var(--text-dark) 0%, #374151 100%)'}}>
        <div className="container">
          <h2 className="display-6 fw-bold mb-3">Ready to Secure Your Business?</h2>
          <p className="lead mb-4">
            Contact us today to learn more about our mobile security solutions and how we can protect your valuable inventory.
          </p>
          
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="contact-info">
                <div className="mb-3 fs-5">
                  <i className="fas fa-globe text-info me-2"></i>
                  www.zartecsolutions.com
                </div>
                <div className="mb-3 fs-5">
                  <i className="fas fa-envelope text-info me-2"></i>
                  sales@zartecsolutions.com
                </div>
                <div className="mb-3 fs-5">
                  <i className="fas fa-phone text-info me-2"></i>
                  +973 33711399
                </div>
                <div className="fs-5">
                  <i className="fas fa-user text-info me-2"></i>
                  Contact: Siju Sreedhar
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Font Awesome CDN */}
      <link 
        rel="stylesheet" 
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" 
      />
    </div>
  );
};

export default Mobilesecurity;